import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IoCheckmarkCircle,
  IoDocumentText,
  IoDownload,
  IoReloadOutline,
  IoAlertCircle,
  IoTimeOutline,
  IoLocationOutline,
  IoCallOutline,
  IoPersonOutline,
  IoBusinessOutline,
  IoCheckmarkDoneOutline,
  IoCalendarOutline,
  IoMedkitOutline,
  IoCubeOutline,
  IoChevronDown,
  IoChevronUp,
  IoChatbubbleOutline,
  IoFolderOutline,
  IoMailOutline,
  IoListOutline,
} from "react-icons/io5";
import toast from "react-hot-toast";
import axiosAuth from "../../../utils/axios";

const STATUS_BADGE = {
  pending_ivr: "bg-yellow-100 text-yellow-800 ring-yellow-200",
  pending:     "bg-orange-100 text-orange-800 ring-orange-200",
  processing:  "bg-blue-100 text-blue-800 ring-blue-200",
  shipped:     "bg-indigo-100 text-indigo-800 ring-indigo-200",
  delivered:   "bg-emerald-100 text-emerald-800 ring-emerald-200",
  cancelled:   "bg-red-100 text-red-800 ring-red-200",
  refunded:    "bg-purple-100 text-purple-800 ring-purple-200",
  failed:      "bg-rose-100 text-rose-800 ring-rose-200",
  resubmitted: "bg-amber-100 text-amber-800 ring-amber-200",
};

const STATUS_OPTIONS = [
  { id: "all",         label: "All" },
  { id: "pending_ivr", label: "Pending IVR" },
  { id: "pending",     label: "Pending" },
  { id: "processing",  label: "Processing" },
  { id: "shipped",     label: "Shipped" },
  { id: "delivered",   label: "Delivered" },
  { id: "cancelled",   label: "Cancelled" },
  { id: "refunded",    label: "Refunded" },
  { id: "failed",      label: "Failed" },
  
];

const WhoBillsBadge = ({ whoBills }) => {
  if (!whoBills) return (
    <span className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1 text-sm font-bold mt-2 bg-slate-100 text-slate-400">
      Billing Unset
    </span>
  );
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1 text-sm font-extrabold mt-2 ${whoBills === "promed" ? "bg-teal-600 text-white" : "bg-amber-500 text-white"}`}>
      <span className="text-base">{whoBills === "promed" ? "🏥" : "👤"}</span>
      {whoBills === "promed" ? "ProMed Bill" : "Physician Bill"}
    </span>
  );
};

const InfoBlock = ({ icon, label, value, subValue }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3">
    <div className="flex items-start gap-3">
      <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-teal-100">
        <span className="text-lg">{icon}</span>
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-slate-500">{label}</p>
        <p className="truncate text-sm font-bold text-slate-900">{value}</p>
        {subValue ? <p className="truncate text-xs text-slate-500">{subValue}</p> : null}
      </div>
    </div>
  </div>
);

const BillerDashboard = () => {
  const [activeTab, setActiveTab] = useState("pending");
  const [orders, setOrders] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
const [approvedOrders, setApprovedOrders] = useState([]);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyStatusFilter, setHistoryStatusFilter] = useState("all");
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openingIvrOrderNumber, setOpeningIvrOrderNumber] = useState(null);
  const [approvingOrderId, setApprovingOrderId] = useState(null);
  const [disapprovingOrderId, setDisapprovingOrderId] = useState(null);
  const [notesMap, setNotesMap] = useState({});
  const [expandedNotes, setExpandedNotes] = useState({});
  const [requestingDoc, setRequestingDoc] = useState({});
  const [billingFilter, setBillingFilter] = useState("all");
  const [editingNotesMap, setEditingNotesMap] = useState({});   // { [orderId]: true/false }
  const [editingNotesValue, setEditingNotesValue] = useState({}); // { [orderId]: "draft text" }
  const [savingNotes, setSavingNotes] = useState({});            // { [orderId]: true/false }
  const [resubmittedOrders, setResubmittedOrders] = useState([]);
  const totalOrders = activeTab === "pending"
  ? pendingOrders.length
  : activeTab === "resubmitted"
  ? resubmittedOrders.length
  : approvedOrders.length;


  const fetchOrders = async (tab, mode = "load") => {
  const isHardLoad = mode === "load";
  const isSoftRefresh = mode === "refresh";

  // ✅ Set loading state BEFORE the async work, not inside it
  if (tab === "history") {
    isHardLoad ? setIsHistoryLoading(true) : setIsRefreshing(true);
  } else {
    isHardLoad ? setIsPageLoading(true) : setIsRefreshing(true);
  }

  try {
    const axiosInstance = axiosAuth();
    if (tab === "history") {
      const response = await axiosInstance.get("biller/orders/history/");
      if (response?.data?.success) {
        setHistoryOrders(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        toast.error("Failed to load order history");
      }
      return;
    }
    if (tab === "resubmitted") {
        const response = await axiosInstance.get("biller/orders/?tab=resubmitted");
        if (response?.data?.success) {
          setResubmittedOrders(Array.isArray(response.data.data) ? response.data.data : []);
        } else {
          toast.error("Failed to load resubmitted orders");
        }
        return;
      }

    
    const endpoint = tab === "pending" ? "biller/orders/?tab=pending" : "biller/orders/?tab=approved";
    const response = await axiosInstance.get(endpoint);

    if (response?.data?.success) {
      const data = Array.isArray(response.data.data) ? response.data.data : [];
      // ✅ Write to the correct bucket — no cross-contamination
      if (tab === "pending") {
        setPendingOrders(data);
      } else {
        setApprovedOrders(data);
      }
    } else {
      toast.error("Failed to load orders");
    }
  } catch (err) {
    console.error("Error fetching biller orders:", err);
    toast.error("Failed to load orders");
  } finally {
    setIsPageLoading(false);
    setIsRefreshing(false);
    setIsHistoryLoading(false);
  }
};

  const handleSaveNotes = async (orderId, orderNumber) => {
  const notes = (editingNotesValue[orderId] || '').trim();
  if (!notes) { toast.error('Notes cannot be empty'); return; }
  try {
    setSavingNotes(prev => ({ ...prev, [orderId]: true }));
    const axiosInstance = axiosAuth();
    const response = await axiosInstance.patch(
      `biller/orders/${orderId}/update-notes/`,
      { medical_specialist_notes: notes }
    );
    if (response?.data?.success) {
      toast.success(`✅ Notes updated for Order #${orderNumber}`);
      // Update local state so the card reflects the change immediately
      setApprovedOrders(prev =>
        prev.map(o => o.id === orderId 
          ? { ...o, medical_specialist_notes: response.data.data.medical_specialist_notes } 
          : o)
      );
      setEditingNotesMap(prev => ({ ...prev, [orderId]: false }));
    } else {
      toast.error(response?.data?.error || 'Failed to update notes');
    }
  } catch (err) {
    toast.error(err?.response?.data?.error || 'Failed to update notes');
  } finally {
    setSavingNotes(prev => ({ ...prev, [orderId]: false }));
  }
};

  useEffect(() => {
    fetchOrders(activeTab, "load");
  }, [activeTab]);

  const handleOpenIVR = async (ivrUrl, orderNumber) => {
    if (!ivrUrl) { toast.error("IVR PDF not available"); return; }
    try {
      setOpeningIvrOrderNumber(orderNumber);
      window.open(ivrUrl, "_blank", "noopener,noreferrer");
      toast.success("Opening IVR PDF...");
    } catch {
      toast.error("Failed to open IVR");
    } finally {
      setOpeningIvrOrderNumber(null);
    }
  };

  const handleApproveIVR = async (orderId, orderNumber) => {
    const notes = notesMap[orderId]?.trim() || '';
    if (!notes) { toast.error('Medical specialist notes are required'); return; }
    try {
      setApprovingOrderId(orderId);
      const axiosInstance = axiosAuth();
      const response = await axiosInstance.post(
        `biller/orders/${orderId}/approve-ivr/`,
        { medical_specialist_notes: notes }
      );
      if (response?.data?.success) {
        toast.success(`✅ IVR approved for Order #${orderNumber}`);
        setNotesMap(prev => { const u = { ...prev }; delete u[orderId]; return u; });
        fetchOrders(activeTab, "refresh");
      } else {
        toast.error(response?.data?.error || "Failed to approve IVR");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to approve IVR");
    } finally {
      setApprovingOrderId(null);
    }
  };

  const handleDisapproveIVR = async (orderId, orderNumber) => {
    const notes = notesMap[orderId]?.trim() || '';
    if (!notes) { toast.error('Medical specialist notes are required'); return; }
    if (!window.confirm(
      `Are you sure you want to disapprove IVR for Order #${orderNumber}?\n\nThe provider and sales rep will be notified to resubmit.`
    )) return;
    try {
      setDisapprovingOrderId(orderId);
      const axiosInstance = axiosAuth();
      const response = await axiosInstance.post(
        `biller/orders/${orderId}/disapprove-ivr/`,
        { medical_specialist_notes: notes }
      );
      if (response?.data?.success) {
        toast.success(`❌ IVR disapproved for Order #${orderNumber}. Notifications sent.`);
        setNotesMap(prev => { const u = { ...prev }; delete u[orderId]; return u; });
        fetchOrders(activeTab, "refresh");
      } else {
        toast.error(response?.data?.error || "Failed to disapprove IVR");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to disapprove IVR");
    } finally {
      setDisapprovingOrderId(null);
    }
  };

  const handleRequestDocument = async (orderId, orderNumber) => {
    try {
      setRequestingDoc(prev => ({ ...prev, [orderId]: true }));
      const axiosInstance = axiosAuth();
      const response = await axiosInstance.post(`biller/orders/${orderId}/request-document/`);
      if (response?.data?.success) {
        toast.success(`📧 Document request sent for Order #${orderNumber}`);
      } else {
        toast.error("Failed to send request");
      }
    } catch {
      toast.error("Failed to send document request");
    } finally {
      setRequestingDoc(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const filteredOrders = useMemo(() => {
    const source = activeTab === "pending"
    ? pendingOrders
    : activeTab === "resubmitted"
    ? resubmittedOrders
    : approvedOrders;
    if (billingFilter === "all") return source;
    if (billingFilter === "promed") return source.filter(o => o.who_bills === "promed");
    if (billingFilter === "self") return source.filter(o => o.who_bills === "self");
    return source;
  }, [pendingOrders, approvedOrders, resubmittedOrders, activeTab, billingFilter]);




  const filteredHistoryOrders = useMemo(() => {
    let result = historyOrders;
    if (historyStatusFilter !== "all") result = result.filter(o => o.status === historyStatusFilter);
    if (billingFilter === "promed") result = result.filter(o => o.who_bills === "promed");
    if (billingFilter === "self") result = result.filter(o => o.who_bills === "self");
    return result;
  }, [historyOrders, historyStatusFilter, billingFilter]);

  const toggleNotes = (orderId) =>
    setExpandedNotes(prev => ({ ...prev, [orderId]: !prev[orderId] }));

  const handleNotesChange = (orderId, value) =>
    setNotesMap(prev => ({ ...prev, [orderId]: value }));

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    try {
      return new Date(dateValue).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      });
    } catch { return "N/A"; }
  };

  const statusUi = (status) => {
    const n = String(status || "").toLowerCase();
    if (n === "approved") return { label: "Approved", pill: "bg-emerald-50 text-emerald-700 ring-emerald-200", icon: <IoCheckmarkCircle className="text-emerald-600" />, dot: "bg-emerald-500" };
    if (n === "rejected" || n === "denied" || n === "declined") return { label: status || "Rejected", pill: "bg-rose-50 text-rose-700 ring-rose-200", icon: <IoAlertCircle className="text-rose-600" />, dot: "bg-rose-500" };
    return { label: status || "Pending", pill: "bg-amber-50 text-amber-700 ring-amber-200", icon: <IoTimeOutline className="text-amber-600" />, dot: "bg-amber-500" };
  };

  const isHistoryTab = activeTab === "history";
  const activeOrders = isHistoryTab ? filteredHistoryOrders : filteredOrders;

  const headerSubtitle = useMemo(() => {
    const count = activeOrders.length;
    if (count === 0) {
      if (isHistoryTab) return "No orders found.";
      if (activeTab === "resubmitted") return "No resubmitted orders right now.";
      return activeTab === "pending" ? "No pending orders right now." : "No approved orders yet.";
    }
    return count === 1 ? "1 order." : `${count} orders.`;
  }, [activeOrders, activeTab, isHistoryTab]);

  const renderOrderCard = (order, index) => {
    const ivrStatus = statusUi(order.ivr_status);
    const isNotesOpen = expandedNotes[order.id];
    const hasNotes = order.salesrep_notes && order.salesrep_notes.trim();
    const hasDocs = order.documents && order.documents.length > 0;
    const createdAt = formatDate(order.created_at);
    const updatedAt = formatDate(order.updated_at);

    return (
      <motion.div
        key={order.id ?? index}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -40 }}
        transition={{ delay: index * 0.03 }}
        className="rounded-2xl border border-slate-200 bg-white/70 shadow-sm backdrop-blur transition hover:shadow-md overflow-hidden"
      >
        <div className="p-6">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">

            {/* LEFT */}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 text-xs font-bold text-teal-700 ring-1 ring-teal-200">
                  <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
                  Guided Order
                </span>

                {/* History: show order status. Pending/Approved: show IVR status */}
                {isHistoryTab ? (
                  <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ring-1 ${STATUS_BADGE[order.status] || "bg-slate-100 text-slate-600 ring-slate-200"}`}>
                    <span className="h-1.5 w-1.5 rounded-full bg-current opacity-50" />
                    {order.status_display || order.status}
                  </span>
                ) : (
                  <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ring-1 ${ivrStatus.pill}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${ivrStatus.dot}`} />
                    {ivrStatus.label}
                  </span>
                )}

                <span className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
                  <IoTimeOutline />
                  {activeTab === "approved" ? `Approved ${updatedAt}` : `Created ${createdAt}`}
                </span>
              </div>

              <div className="mt-4">
                <h3 className="text-2xl font-extrabold tracking-tight text-slate-900">
                  Order #{order.order_number}
                </h3>
                <WhoBillsBadge whoBills={order.who_bills} />
                <p className="mt-1 text-sm text-slate-600">
                  {isHistoryTab
                    ? "Historical order record."
                    : activeTab === "approved"
                    ? "Approved order with IVR documentation."
                    : activeTab === "resubmitted"
                    ? "Provider has resubmitted documents for review."
                    : "Provider order review and IVR access."}
                </p>
              </div>

              {/* Info grid */}
              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                <InfoBlock icon={<IoBusinessOutline />} label="Provider" value={order.provider_name || "N/A"} subValue={order.provider_email || ""} />
                <InfoBlock icon={<IoPersonOutline />} label="Patient" value={order.patient_name || "N/A"} />
                <InfoBlock
                  icon={<IoDocumentText />}
                  label="Care Type"
                  value={
                    (order.care_type === "propacks" || (!order.care_type && order.conservative_care))
                      ? "ProPacks (Conservative)"
                      : "Advanced Care"
                  }
                />
                <InfoBlock icon={<IoCalendarOutline />} label="Kit Duration" value={order.kit_duration || "N/A"} />
                <InfoBlock icon={<IoAlertCircle />} label="Wound Type" value={order.wound_type || "N/A"} />
                {order.wound_location && (
                  <InfoBlock icon={<IoLocationOutline />} label="Wound Location" value={order.wound_location} />
                )}

                {(order.care_type === "propacks" || (!order.care_type && order.conservative_care)) && order.propack_config && (
                  <div className="md:col-span-2">
                    <div className="rounded-xl border border-slate-200 bg-white p-3">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-teal-100">
                          <IoCubeOutline className="text-lg" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-slate-500">ProPack Kit</p>
                          <div className="mt-2 space-y-2">
                            {order.propack_config.primary && (
                              <div className="rounded-lg bg-slate-50 px-3 py-2">
                                <p className="text-xs font-semibold text-slate-600">Primary</p>
                                <p className="text-sm font-bold text-slate-900">
                                  {order.propack_config.primary.type?.replace(/_/g, ' ')?.toUpperCase() || "N/A"}
                                  {order.propack_config.primary.size && ` - ${order.propack_config.primary.size}`}
                                </p>
                              </div>
                            )}
                            {order.propack_config.secondary && (
                              <div className="rounded-lg bg-slate-50 px-3 py-2">
                                <p className="text-xs font-semibold text-slate-600">Secondary</p>
                                <p className="text-sm font-bold text-slate-900">
                                  {order.propack_config.secondary.type?.replace(/_/g, ' ')?.toUpperCase() || "N/A"}
                                  {order.propack_config.secondary.size && ` - ${order.propack_config.secondary.size}`}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {(order.care_type === "advanced" || (!order.care_type && !order.conservative_care)) && (order.graft_name || order.graft_size) && (
                  <>
                    <InfoBlock icon={<IoMedkitOutline />} label="Graft Name" value={order.graft_name || "N/A"} />
                    <InfoBlock icon={<IoCubeOutline />} label="Graft Size" value={order.graft_size || "N/A"} />
                  </>
                )}

                <InfoBlock icon={<IoBusinessOutline />} label="Facility" value={order.facility_name || "N/A"} />
                <div className="md:col-span-2">
                  <InfoBlock
                    icon={<IoLocationOutline />}
                    label="Delivery Address"
                    value={`${order.street || ""}${order.street ? ", " : ""}${order.city || ""}${order.city ? ", " : ""}${order.state || ""} ${order.zip_code || ""}`.trim() || "N/A"}
                  />
                </div>
                <InfoBlock icon={<IoCallOutline />} label="Phone" value={order.phone_number || "N/A"} />
                {activeTab === "resubmitted" && order.resubmit_doc_url && (
                  <div className="md:col-span-2">
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="grid h-9 w-9 place-items-center rounded-xl bg-amber-100 ring-1 ring-amber-200">
                            <IoDocumentText className="text-lg text-amber-700" />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-500">Resubmitted Document</p>
                            <p className="text-sm font-bold text-slate-900">Provider uploaded a new document</p>
                          </div>
                        </div>
                        <a
                          href={order.resubmit_doc_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700 transition-colors"
                        >
                          <IoDownload className="text-sm" />
                          View
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* Replace the existing medical_specialist_notes InfoBlock with this */}
{(activeTab === "approved" || activeTab === "resubmitted" || isHistoryTab) && order.medical_specialist_notes && (
  <div className="md:col-span-2">
    {activeTab === "approved" && editingNotesMap[order.id] ? (
      // ── Edit mode ──
      <div className="rounded-xl border border-teal-200 bg-teal-50/40 p-4 space-y-3">
        <p className="text-xs font-bold text-teal-700 uppercase tracking-wide flex items-center gap-1.5">
          <IoDocumentText className="text-sm" /> Add new note
        </p>
        <textarea
          placeholder="Add a new note entry"
          value={editingNotesValue[order.id] ?? order.medical_specialist_notes}
          onChange={e =>
            setEditingNotesValue(prev => ({ ...prev, [order.id]: e.target.value }))
          }
          rows={4}
          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
        />
        {!(editingNotesValue[order.id] ?? order.medical_specialist_notes).trim() && (
          <p className="text-xs text-amber-600 font-semibold">Notes cannot be empty</p>
        )}
        <div className="flex gap-2">
          <button
            onClick={() => handleSaveNotes(order.id, order.order_number)}
            disabled={
              savingNotes[order.id] ||
              !(editingNotesValue[order.id] ?? '').trim()
            }
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-2 text-sm font-extrabold text-white shadow-sm transition hover:from-teal-700 hover:to-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {savingNotes[order.id] ? (
              <><motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="inline-flex"><span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent" /></motion.span>Saving...</>
            ) : (
              <><IoCheckmarkCircle className="text-base" />Save Notes</>
            )}
          </button>
          <button
            onClick={() => {
              setEditingNotesMap(prev => ({ ...prev, [order.id]: false }));
              setEditingNotesValue(prev => ({ ...prev, [order.id]: order.medical_specialist_notes }));
            }}
            disabled={savingNotes[order.id]}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </div>
    ) : (
      // ── Read mode ──
       <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 grid h-9 w-9 place-items-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-teal-100">
            <IoDocumentText className="text-lg" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-500">Medical Specialist Notes</p>
              {activeTab === "approved" && (
                <button
                  onClick={() => {
                    setEditingNotesMap(prev => ({ ...prev, [order.id]: true }));
                    setEditingNotesValue(prev => ({ ...prev, [order.id]: '' })); // ✅ start fresh
                  }}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-600 transition hover:border-teal-400 hover:text-teal-700"
                >
                  ✏️ Add Note
                </button>
              )}
            </div>

            {/* ✅ Render each note as a timeline entry */}
            <div className="mt-2 space-y-3">
              {order.medical_specialist_notes.split('\n\n---\n\n').map((entry, i, arr) => {
                const lines = entry.split('\n');
                const timestamp = lines[0]?.startsWith('[') ? lines[0] : null;
                const noteText = timestamp ? lines.slice(1).join('\n') : entry;
                return (
                  <div key={i} className="relative pl-4 border-l-2 border-teal-200">
                    {timestamp && (
                      <p className="text-xs text-slate-400 font-semibold mb-0.5">{timestamp.replace(/\[|\]/g, '')}</p>
                    )}
                    <p className="text-sm font-bold text-slate-900 whitespace-pre-wrap">{noteText}</p>
                    {i < arr.length - 1 && <div className="mt-2" />}
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </div>
    )}
  </div>
)}
              </div>
            </div>

            {/* RIGHT: IVR Panel — pending tab only */}
            {(activeTab === "pending" || activeTab === "resubmitted") && (
              <div className="w-full lg:w-[310px]">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="grid h-9 w-9 place-items-center rounded-xl bg-teal-50 ring-1 ring-teal-100">
                        <IoDocumentText className="text-lg text-teal-700" />
                      </div>
                      <div>
                        <p className="text-sm font-extrabold text-slate-900">IVR Form</p>
                        <p className="text-xs text-slate-500">Status & download</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-extrabold ring-1 ${ivrStatus.pill}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${ivrStatus.dot}`} />
                      {ivrStatus.label}
                    </span>
                  </div>

                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                    {order.has_ivr ? (
                      <>
                        <div className="flex items-center gap-2">
                          {ivrStatus.icon}
                          <p className="text-sm font-semibold text-slate-800">{ivrStatus.label}</p>
                        </div>
                        <button
                          onClick={() => handleOpenIVR(order.ivr_download_url, order.order_number)}
                          disabled={openingIvrOrderNumber === order.order_number || !order.ivr_download_url}
                          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-3 text-sm font-extrabold text-white shadow-sm transition hover:from-teal-700 hover:to-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {openingIvrOrderNumber === order.order_number ? (
                            <><motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="inline-flex"><span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent" /></motion.span>Opening...</>
                          ) : (
                            <><IoDownload className="text-lg" />Download IVR PDF</>
                          )}
                        </button>

                        
                          <div className="mt-4 space-y-3">
                            <div>
                              <label htmlFor={`notes-${order.id}`} className="block text-xs font-semibold text-slate-700 mb-2">
                                Medical Specialist Notes *
                              </label>
                              <textarea
                                id={`notes-${order.id}`}
                                value={notesMap[order.id] || ''}
                                onChange={(e) => handleNotesChange(order.id, e.target.value)}
                                placeholder="Enter notes before approving/disapproving..."
                                rows={4}
                                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                              />
                            </div>
                            <button
                              onClick={() => handleApproveIVR(order.id, order.order_number)}
                              disabled={approvingOrderId === order.id || disapprovingOrderId === order.id || !notesMap[order.id]?.trim()}
                              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 px-4 py-3 text-sm font-extrabold text-white shadow-sm transition hover:from-emerald-600 hover:to-green-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {approvingOrderId === order.id ? (
                                <><motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="inline-flex"><span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent" /></motion.span>Approving...</>
                              ) : (
                                <><IoCheckmarkCircle className="text-lg" />Approve IVR</>
                              )}
                            </button>
                            <button
                              onClick={() => handleDisapproveIVR(order.id, order.order_number)}
                              disabled={approvingOrderId === order.id || disapprovingOrderId === order.id || !notesMap[order.id]?.trim()}
                              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-rose-500 to-red-600 px-4 py-3 text-sm font-extrabold text-white shadow-sm transition hover:from-rose-600 hover:to-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {disapprovingOrderId === order.id ? (
                                <><motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="inline-flex"><span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent" /></motion.span>Disapproving...</>
                              ) : (
                                <><IoAlertCircle className="text-lg" />Disapprove & Request Resubmission</>
                              )}
                            </button>
                            {!notesMap[order.id]?.trim() && (
                              <p className="text-xs text-amber-600 font-semibold text-center">
                                Please enter notes to enable approval/disapproval
                              </p>
                            )}
                          </div>
                        
                      </>
                    ) : (
                      <div className="py-4 text-center">
                        <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-white ring-1 ring-slate-200">
                          <IoAlertCircle className="text-2xl text-slate-400" />
                        </div>
                        <p className="mt-3 text-sm font-bold text-slate-800">No IVR Form</p>
                        <p className="mt-1 text-xs text-slate-500">Not submitted yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notes & Documents expandable */}
        <div className="border-t border-slate-100">
          <button
            onClick={() => toggleNotes(order.id)}
            className="w-full flex items-center justify-between px-6 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <IoFolderOutline className="text-teal-600 text-base" />
              <span>Notes & Documents</span>
              <div className="flex items-center gap-1.5 ml-2">
                {hasNotes && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200">
                    <IoChatbubbleOutline className="text-xs" /> Notes
                  </span>
                )}
                {hasDocs && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-xs font-bold text-teal-700 ring-1 ring-teal-200">
                    <IoDocumentText className="text-xs" /> {order.documents.length} doc{order.documents.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            {isNotesOpen ? <IoChevronUp className="text-slate-400" /> : <IoChevronDown className="text-slate-400" />}
          </button>

          <AnimatePresence>
            {isNotesOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-6 space-y-4">
                  {hasNotes ? (
                    <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="grid h-7 w-7 place-items-center rounded-lg bg-indigo-100 text-indigo-700">
                          <IoChatbubbleOutline className="text-sm" />
                        </div>
                        <p className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Sales Rep Notes</p>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{order.salesrep_notes}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic">No sales rep notes for this order.</p>
                  )}

                  {hasDocs ? (
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                        <IoFolderOutline className="text-teal-600" /> Uploaded Documents
                      </p>
                      {order.documents.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="grid h-9 w-9 place-items-center rounded-xl bg-teal-50 ring-1 ring-teal-100 flex-shrink-0">
                              <IoDocumentText className="text-teal-700" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-slate-900 truncate">{doc.title}</p>
                              <p className="text-xs text-slate-500">
                                {doc.document_type_display} • {doc.file_size_mb} MB • {new Date(doc.uploaded_at).toLocaleDateString()}
                              </p>
                              {doc.notes && <p className="text-xs text-slate-400 mt-0.5 truncate">{doc.notes}</p>}
                            </div>
                          </div>
                          {doc.file_url && (
                            <a
                              href={doc.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-3 flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-teal-700 transition-colors"
                            >
                              <IoDownload className="text-sm" />
                              View
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-center">
                      <IoFolderOutline className="text-2xl text-slate-300 mx-auto mb-1" />
                      <p className="text-xs font-semibold text-slate-500">No documents uploaded yet</p>
                    </div>
                  )}

                  <button
                    onClick={() => handleRequestDocument(order.id, order.order_number)}
                    disabled={requestingDoc[order.id]}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-teal-400 hover:text-teal-700 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {requestingDoc[order.id] ? (
                      <><motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="inline-flex"><span className="h-4 w-4 rounded-full border-2 border-slate-400 border-t-transparent" /></motion.span>Sending Request...</>
                    ) : (
                      <><IoMailOutline className="text-lg" />Request Document</>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    );
  };

  if (isPageLoading && !isHistoryTab) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-teal-50/40 to-emerald-50/30 flex items-center justify-center">
        <div className="rounded-2xl border border-teal-100 bg-white/70 p-8 shadow-sm backdrop-blur flex items-center gap-4">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="h-10 w-10 rounded-full border-4 border-teal-100 border-t-teal-600" />
          <div>
            <p className="text-sm font-semibold text-slate-900">Loading orders</p>
            <p className="text-sm text-slate-600">Fetching billing data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-teal-50/40 to-emerald-50/30">
      <div className="mx-auto max-w-7xl px-6 py-8">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white/70 px-3 py-1 text-xs font-semibold text-teal-700 shadow-sm backdrop-blur">
                <span className="h-2 w-2 rounded-full bg-teal-500" />
                Biller Console • Guided Orders
              </div>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                {activeTab === "pending" ? "Pending" : activeTab === "approved" ? "Approved" : activeTab === "resubmitted" ? "Resubmitted" : "Order History"} Guided Orders
              </h1>
              <p className="mt-2 text-sm text-slate-600 sm:text-base">
                {activeTab === "pending"
                  ? "Review and approve pending IVR orders."
                  : activeTab === "approved"
                  ? "View approved orders ready for billing."
                  : "All guided orders across every status."}{" "}
                <span className="font-semibold text-slate-700">{headerSubtitle}</span>
              </p>
            </div>

            <button
              onClick={() => fetchOrders(activeTab, "refresh")}
              disabled={isRefreshing || isHistoryLoading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
            >
              <motion.span
                animate={(isRefreshing || isHistoryLoading) ? { rotate: 360 } : { rotate: 0 }}
                transition={(isRefreshing || isHistoryLoading) ? { duration: 0.9, repeat: Infinity, ease: "linear" } : { duration: 0.2 }}
                className="inline-flex"
              >
                <IoReloadOutline className="text-teal-600" />
              </motion.span>
              {(isRefreshing || isHistoryLoading) ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex gap-2 p-1.5 rounded-2xl bg-slate-100 dark:bg-slate-900/50 w-fit">
            {[
              { id: "pending",  label: "Pending IVR",   icon: <IoTimeOutline className="text-lg" /> },
              { id: "approved", label: "Approved IVR",  icon: <IoCheckmarkDoneOutline className="text-lg" /> },
               { id: "resubmitted", label: "Resubmitted", icon: <IoReloadOutline className="text-lg" /> },
              { id: "history",  label: "Order History", icon: <IoListOutline className="text-lg" /> },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-6 py-2.5 rounded-xl text-sm font-extrabold transition-all ${
                  activeTab === tab.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <span className="inline-flex items-center gap-2">{tab.icon}{tab.label}</span>
              </button>
            ))}
          </div>

          {/* KPI */}
          <div className="mt-6 rounded-3xl border border-teal-100 bg-white/70 p-5 shadow-sm backdrop-blur relative overflow-hidden">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500">
                  {isHistoryTab ? "Total Orders" : activeTab === "pending" ? "Total Pending Orders" : "Total Approved Orders"}
                </p>
                <p className="mt-2 text-4xl font-extrabold tracking-tight text-slate-900">
                  {isHistoryTab ? filteredHistoryOrders.length : totalOrders}
                </p>
              </div>
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-teal-50 ring-1 ring-teal-100">
                {activeTab === "pending"
                  ? <IoDocumentText className="text-xl text-teal-700" />
                  : activeTab === "approved"
                  ? <IoCheckmarkDoneOutline className="text-xl text-teal-700" />
                  : <IoListOutline className="text-xl text-teal-700" />}
              </div>
            </div>
            <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-gradient-to-br from-teal-200/40 to-emerald-200/30 blur-2xl" />
          </div>

          {/* Status filter — history tab only */}
          {isHistoryTab && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wide mr-1">Status:</span>
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setHistoryStatusFilter(s.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-extrabold transition-all border ${
                    historyStatusFilter === s.id
                      ? "bg-slate-800 text-white border-slate-800 shadow-sm"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {s.label}
                  {s.id !== "all" && (
                    <span className="ml-1.5 opacity-70">
                      ({historyOrders.filter(o => o.status === s.id).length})
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Billing filter */}
          <div className="mt-4 flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide mr-1">Billing:</span>
            {[
              { id: "all",    label: "All" },
              { id: "promed", label: "ProMed Bill" },
              { id: "self",   label: "Physician Bill" },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setBillingFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-extrabold transition-all border ${
                  billingFilter === f.id
                    ? f.id === "promed"
                      ? "bg-teal-600 text-white border-teal-600 shadow-sm"
                      : f.id === "self"
                      ? "bg-amber-500 text-white border-amber-500 shadow-sm"
                      : "bg-slate-800 text-white border-slate-800 shadow-sm"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
              >
                {f.label}
                {f.id !== "all" && (
                  <span className="ml-1.5 opacity-80">
                    ({(isHistoryTab ? historyOrders : activeTab === "pending" ? pendingOrders : approvedOrders).filter(o =>
                      f.id === "promed" ? o.who_bills === "promed" : o.who_bills === "self"
                    ).length})
                  </span>
                )}
              </button>
            ))}
          </div>
        </motion.div>

        {/* History loading */}
        {isHistoryTab && isHistoryLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="rounded-2xl border border-teal-100 bg-white/70 p-8 shadow-sm backdrop-blur flex items-center gap-4">
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="h-10 w-10 rounded-full border-4 border-teal-100 border-t-teal-600" />
              <p className="text-sm font-semibold text-slate-900">Loading order history...</p>
            </div>
          </div>
        ) : activeOrders.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="rounded-2xl border border-slate-200 bg-white/70 p-10 text-center shadow-sm backdrop-blur"
          >
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-slate-50 ring-1 ring-slate-200">
              {isHistoryTab
                ? <IoListOutline className="text-3xl text-slate-400" />
                : activeTab === "pending"
                ? <IoCheckmarkCircle className="text-3xl text-emerald-600" />
                : activeTab === "resubmitted"
                ? <IoReloadOutline className="text-3xl text-amber-500" />  // ✅
                : <IoDocumentText className="text-3xl text-slate-400" />}
            </div>
            <h3 className="mt-4 text-xl font-bold text-slate-900">
              {isHistoryTab ? "No orders found"
                : activeTab === "pending" ? "All caught up"
                : activeTab === "resubmitted" ? "No resubmitted orders"  // ✅
                : "No approved orders"}
            </h3>
            <p className="mt-2 text-slate-600">
              {isHistoryTab
                ? "No orders match the current filters."
                : activeTab === "pending"
                ? "No pending guided orders at the moment."
                : activeTab === "resubmitted"
                ? "No resubmitted orders at the moment."
                : "No orders have been approved yet."}
            </p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {activeOrders.map((order, index) => renderOrderCard(order, index))}
            </AnimatePresence>
          </div>
        )}

      </div>
    </div>
  );
};

export default BillerDashboard;